import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CanvasEditor - OpenAI Canvas-style code/text editor with live AI editing
 *
 * Replicates the EXACT visual effect from ChatGPT Canvas.
 * Uses a built-in simple diff algorithm to avoid external dependencies.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
// ============================================
// COLORS - Exact Canvas palette
// ============================================
const CANVAS_COLORS = {
    bg: {
        scanning: 'rgba(254, 249, 195, 0.4)',
        modifying: 'rgba(254, 249, 195, 0.7)',
        added: 'rgba(220, 252, 231, 0.7)',
        removed: 'rgba(254, 226, 226, 0.7)',
        complete: 'rgba(220, 252, 231, 0.3)',
        idle: 'transparent',
    },
    bullet: {
        scanning: '#fbbf24',
        modifying: '#f59e0b',
        added: '#22c55e',
        removed: '#ef4444',
        complete: '#86efac',
        idle: 'transparent',
    },
    action: {
        add: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: '✅' },
        modify: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: '✏️' },
        remove: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: '❌' },
        analyze: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', icon: '🔍' },
        fix: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', icon: '🔧' },
    },
};
/**
 * Simple character-level diff using LCS approach
 * Returns array of char diffs with types: -1 (delete), 0 (equal), 1 (insert)
 */
function getCharDiffs(oldText, newText) {
    if (oldText === newText) {
        return newText.split('').map((char, idx) => ({
            type: 0,
            char,
            key: `eq-${idx}`,
        }));
    }
    if (!oldText) {
        return newText.split('').map((char, idx) => ({
            type: 1,
            char,
            key: `ins-${idx}`,
        }));
    }
    if (!newText) {
        return oldText.split('').map((char, idx) => ({
            type: -1,
            char,
            key: `del-${idx}`,
        }));
    }
    // Find common prefix
    let prefixLen = 0;
    const minLen = Math.min(oldText.length, newText.length);
    while (prefixLen < minLen && oldText[prefixLen] === newText[prefixLen]) {
        prefixLen++;
    }
    // Find common suffix (after prefix)
    let suffixLen = 0;
    while (suffixLen < minLen - prefixLen &&
        oldText[oldText.length - 1 - suffixLen] === newText[newText.length - 1 - suffixLen]) {
        suffixLen++;
    }
    const result = [];
    // Add prefix (equal)
    for (let i = 0; i < prefixLen; i++) {
        result.push({ type: 0, char: oldText[i], key: `pf-${i}` });
    }
    // Add deleted middle
    const oldMiddle = oldText.slice(prefixLen, oldText.length - suffixLen);
    for (let i = 0; i < oldMiddle.length; i++) {
        result.push({ type: -1, char: oldMiddle[i], key: `del-${i}` });
    }
    // Add inserted middle
    const newMiddle = newText.slice(prefixLen, newText.length - suffixLen);
    for (let i = 0; i < newMiddle.length; i++) {
        result.push({ type: 1, char: newMiddle[i], key: `ins-${i}` });
    }
    // Add suffix (equal)
    const suffix = newText.slice(newText.length - suffixLen);
    for (let i = 0; i < suffix.length; i++) {
        result.push({ type: 0, char: suffix[i], key: `sf-${i}` });
    }
    return result;
}
// ============================================
// ANIMATED CHARACTER
// ============================================
const AnimatedCharacter = React.memo(({ diff, delay, speed, }) => {
    const { type, char } = diff;
    if (type === 0) {
        return _jsx("span", { className: "whitespace-pre", children: char });
    }
    if (type === -1) {
        return (_jsx(motion.span, { initial: { opacity: 1, width: 'auto' }, animate: { opacity: 0, width: 0 }, transition: {
                duration: 0.12,
                delay: delay * (speed / 1000),
                ease: 'easeOut',
            }, className: "inline-block overflow-hidden whitespace-pre text-red-600 line-through", style: { backgroundColor: CANVAS_COLORS.bg.removed }, children: char }));
    }
    return (_jsx(motion.span, { initial: { opacity: 0, width: 0 }, animate: { opacity: 1, width: 'auto' }, transition: {
            duration: 0.12,
            delay: delay * (speed / 1000),
            ease: 'easeOut',
        }, className: "inline-block overflow-hidden whitespace-pre", children: char }));
});
AnimatedCharacter.displayName = 'AnimatedCharacter';
// ============================================
// EDITOR LINE COMPONENT
// ============================================
const EditorLineComponent = React.memo(({ line, isActive, charSpeed, }) => {
    const { content, previousContent, status = 'idle', indent = 0 } = line;
    const charDiffs = useMemo(() => {
        if (previousContent && previousContent !== content) {
            return getCharDiffs(previousContent, content);
        }
        return content.split('').map((char, idx) => ({
            type: 0,
            char,
            key: `s-${idx}`,
        }));
    }, [content, previousContent]);
    const bgColor = CANVAS_COLORS.bg[status];
    const bulletColor = CANVAS_COLORS.bullet[status];
    return (_jsxs(motion.div, { layout: true, className: "relative group", initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0, height: 0 }, children: [_jsx(motion.div, { className: "absolute -inset-x-6 inset-y-0 -z-10", initial: false, animate: { backgroundColor: bgColor }, transition: { duration: 0.2 } }), _jsx(motion.div, { className: "absolute -left-4 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full", initial: { scale: 0 }, animate: {
                    scale: status !== 'idle' ? 1 : 0,
                    backgroundColor: bulletColor,
                }, transition: { duration: 0.15 } }), isActive && (_jsx(motion.div, { className: "absolute left-0 top-0 bottom-0 w-0.5 bg-primary-500", layoutId: "active-indicator" })), _jsx("div", { className: "py-0.5 pr-4 font-mono text-sm leading-relaxed text-slate-800 dark:text-slate-200", style: { paddingLeft: `${indent * 1.5}rem` }, children: charDiffs.map((diff, idx) => {
                    const delay = charDiffs.slice(0, idx).filter(d => d.type !== 0).length;
                    return (_jsx(AnimatedCharacter, { diff: diff, delay: delay, speed: charSpeed }, diff.key));
                }) })] }));
});
EditorLineComponent.displayName = 'EditorLineComponent';
// ============================================
// EDIT HISTORY SIDEBAR
// ============================================
const EditHistorySidebar = ({ history, onToggleExpand, }) => {
    return (_jsx("aside", { className: "w-72 border-r border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 overflow-y-auto", children: _jsxs("div", { className: "p-4", children: [_jsx("h3", { className: "text-sm font-medium text-slate-600 dark:text-slate-400 mb-3", children: "Cambios realizados" }), _jsx("div", { className: "space-y-2", children: _jsx(AnimatePresence, { mode: "popLayout", children: history.slice().reverse().map((action) => {
                            const colors = CANVAS_COLORS.action[action.type];
                            return (_jsxs(motion.div, { initial: { opacity: 0, x: -20, scale: 0.95 }, animate: { opacity: 1, x: 0, scale: 1 }, exit: { opacity: 0, scale: 0.95 }, className: `rounded-lg border border-slate-200/50 dark:border-slate-700/50 overflow-hidden ${colors.bg}`, children: [_jsxs("button", { onClick: () => onToggleExpand?.(action.id), className: "w-full p-3 text-left flex items-start gap-2", children: [_jsx("span", { className: "text-base flex-shrink-0", children: colors.icon }), _jsx("span", { className: `text-sm ${colors.text} leading-snug flex-1`, children: action.description }), action.details && action.details.length > 0 && (_jsx(motion.span, { animate: { rotate: action.expanded ? 90 : 0 }, className: "text-slate-400 text-xs", children: "\u25B6" }))] }), _jsx(AnimatePresence, { children: action.expanded && action.details && (_jsx(motion.div, { initial: { height: 0, opacity: 0 }, animate: { height: 'auto', opacity: 1 }, exit: { height: 0, opacity: 0 }, className: "px-3 pb-3", children: _jsx("ul", { className: "text-xs text-slate-600 dark:text-slate-400 space-y-1 ml-6", children: action.details.map((detail, idx) => (_jsx("li", { className: "list-disc", children: detail }, idx))) }) })) })] }, action.id));
                        }) }) })] }) }));
};
// ============================================
// PROGRESS HEADER
// ============================================
const ProgressHeader = ({ progress, statusMessage, isVisible, }) => {
    if (!isVisible)
        return null;
    return (_jsxs(motion.div, { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 }, className: "px-6 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700", children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(motion.span, { animate: { rotate: 360 }, transition: { duration: 1, repeat: Infinity, ease: 'linear' }, className: "text-lg", children: "\u26A1" }), _jsx("span", { className: "text-sm text-slate-600 dark:text-slate-400", children: statusMessage || 'Editando...' })] }), _jsxs("span", { className: "text-xs text-slate-500 tabular-nums font-medium", children: [Math.round(progress), "%"] })] }), _jsx("div", { className: "h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden", children: _jsx(motion.div, { className: "h-full rounded-full bg-gradient-to-r from-amber-400 via-green-400 to-primary-500", initial: { width: 0 }, animate: { width: `${progress}%` }, transition: { duration: 0.3 } }) })] }));
};
// ============================================
// MAIN CANVAS EDITOR COMPONENT
// ============================================
export function CanvasEditor({ lines, editHistory = [], statusMessage, isEditing, progress = 0, showSidebar = true, charSpeed = 25, className = '', }) {
    const contentRef = useRef(null);
    const [localHistory, setLocalHistory] = useState(editHistory);
    useEffect(() => {
        setLocalHistory(editHistory);
    }, [editHistory]);
    const handleToggleExpand = useCallback((id) => {
        setLocalHistory(prev => prev.map(action => action.id === id ? { ...action, expanded: !action.expanded } : action));
    }, []);
    const activeLineIndex = useMemo(() => lines.findIndex(l => l.status && l.status !== 'idle' && l.status !== 'complete'), [lines]);
    useEffect(() => {
        if (activeLineIndex >= 0 && contentRef.current) {
            const lineEl = contentRef.current.children[activeLineIndex];
            if (lineEl) {
                lineEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [activeLineIndex]);
    return (_jsxs("div", { className: `flex h-full bg-white dark:bg-slate-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg ${className}`, children: [_jsx(AnimatePresence, { children: showSidebar && isEditing && localHistory.length > 0 && (_jsx(motion.div, { initial: { width: 0, opacity: 0 }, animate: { width: 288, opacity: 1 }, exit: { width: 0, opacity: 0 }, transition: { duration: 0.2 }, children: _jsx(EditHistorySidebar, { history: localHistory, onToggleExpand: handleToggleExpand }) })) }), _jsxs("div", { className: "flex-1 flex flex-col overflow-hidden", children: [_jsx(AnimatePresence, { children: isEditing && (_jsx(ProgressHeader, { progress: progress, statusMessage: statusMessage, isVisible: true })) }), _jsx("div", { ref: contentRef, className: "flex-1 overflow-y-auto px-8 py-6", children: _jsx(LayoutGroup, { children: _jsx("div", { className: "max-w-4xl mx-auto pl-6", children: _jsx(AnimatePresence, { mode: "popLayout", children: lines.map((line, idx) => (_jsx(EditorLineComponent, { line: line, isActive: idx === activeLineIndex, charSpeed: charSpeed }, line.id))) }) }) }) })] })] }));
}
export default CanvasEditor;
